export const getFieldValue = (field) => {
  if (field && typeof field === 'object' && Object.prototype.hasOwnProperty.call(field, 'value')) {
    return field.value;
  }

  return field;
};

// Returns the PascalCase Address detail object regardless of whether it is
// passed as a raw API response (`{ Address }`) or a stored value (`{ details }`).
export const getAddressDetails = (address) => address?.details?.Address ?? address?.Address ?? null;

export const getSuccessfulAddressDetails = (address) => {
  const wrapper = address?.details ?? address;
  if (!wrapper?.Address) return null;

  return wrapper.Status === 'Success' ? wrapper : null;
};

export const isValidatedAddressValue = (address, isValidated) =>
  Boolean(isValidated || getFieldValue(address?.validated) || getSuccessfulAddressDetails(address));

/** @param {any} address */
export const getAddressEntryMode = (address) => (address?.enterManually ? 'manual' : 'auto');

/** @param {any} address */
export const hasAddressValue = (address) =>
  Boolean(address && (isValidatedAddressValue(address) || address.lookupValue || address.enterManually));

/**
 * @param {any} address
 * @param {string} entryMode
 */
export const withAddressEntryMode = (address, entryMode) => {
  if (!hasAddressValue(address)) return address;

  return {
    ...address,
    enterManually: entryMode === 'manual',
  };
};

export const isPoBoxAddress = (address = {}) => {
  const details = getAddressDetails(address);
  if (details) return Boolean(details.PostalDeliveryType);

  return Boolean(
    address.enterManually && address.addressLine && address.addressLine.trim().toLowerCase().startsWith('po box'),
  );
};

export const isPhysicalAddress = (address) =>
  Boolean(
    address &&
      (getAddressDetails(address) || (address.enterManually && address.addressLine)) &&
      !isPoBoxAddress(address),
  );

const toField = (value) => ({ value: value ?? '' });

const getStateValue = (state) => {
  if (!state) return '';
  if (typeof state === 'string') return state;

  return state.text || state.label || state.value || '';
};

// Converts a stored (legacy) address value into the field model consumed by the
// AutoSuggestAddress component.
/** @param {any} address */
export const toAutoSuggestAddressValue = (address = {}, errorMessage = '') => {
  const details = getAddressDetails(address);
  const state = getStateValue(address?.state) || details?.State || '';
  const formattedAddress =
    address?.lookupValue?.label ||
    address?.formattedAddress ||
    details?.PartialAddress ||
    [
      address?.addressLine || details?.AddressLine1,
      address?.suburb || details?.Suburb,
      state,
      address?.postcode || details?.Postcode,
    ]
      .filter(Boolean)
      .join(' ');

  return {
    formattedAddress: { ...toField(formattedAddress), errorMessage },
    propertyName: toField(address?.propertyName || details?.PropertyName),
    streetAddress: toField(address?.addressLine || details?.AddressLine1),
    suburb: toField(address?.suburb || details?.Suburb),
    state: toField(state),
    postcode: toField(address?.postcode || details?.Postcode),
    country: toField(address?.country || details?.Country || 'Australia'),
    nswPointId: toField(address?.lookupValue?.value || details?.UniqueId),
    validated: toField(Boolean(getSuccessfulAddressDetails(address))),
  };
};

const comparableAddressFields = ['propertyName', 'streetAddress', 'suburb', 'state', 'postcode', 'country'];

/** @param {any} value */
const normalizeComparableValue = (value) => `${value ?? ''}`.trim().replace(/\s+/g, ' ');

/**
 * @param {any} model
 * @param {string} fieldName
 */
const getComparableAddressValue = (model, fieldName) => {
  if (fieldName === 'streetAddress') {
    return normalizeComparableValue(getFieldValue(model?.streetAddress) || getFieldValue(model?.formattedAddress));
  }

  return normalizeComparableValue(getFieldValue(model?.[fieldName]));
};

/**
 * @param {any} nextAddress
 * @param {any} currentAddress
 */
export const hasAddressContentChanged = (nextAddress, currentAddress) => {
  const currentAddressValue = toAutoSuggestAddressValue(currentAddress);

  return comparableAddressFields.some(
    (fieldName) =>
      getComparableAddressValue(nextAddress, fieldName) !== getComparableAddressValue(currentAddressValue, fieldName),
  );
};

/**
 * @param {boolean} isEntryModeChanging
 * @param {any} nextAddress
 * @param {any} currentAddress
 * @param {string | null | undefined} changedPropertyName
 * @param {boolean | undefined} isValidated
 */
export const isUnchangedEntryModeAddressEvent = (
  isEntryModeChanging,
  nextAddress,
  currentAddress,
  changedPropertyName,
  isValidated,
) => {
  if (!isEntryModeChanging || isValidated || changedPropertyName) return false;

  return !hasAddressContentChanged(nextAddress, currentAddress);
};

export const shouldUseFormattedAddressOnly = (isManualAddress, changedPropertyName, isValidAddress) =>
  !isManualAddress && changedPropertyName === 'formattedAddress' && !isValidAddress;

// Converts the field model emitted by the AutoSuggestAddress component (plus the
// full detail captured on selection) into the stored (legacy) address value.
export const toLegacyAddressValue = (model, isManualAddress, selectedDetails, useFormattedAddressOnly = false) => {
  const detailsAddress = selectedDetails?.Address;
  const formattedAddress = getFieldValue(model?.formattedAddress) || detailsAddress?.PartialAddress || '';

  if (useFormattedAddressOnly) {
    return {
      enterManually: isManualAddress,
      addressLine: formattedAddress,
      suburb: '',
      state: { label: '', text: '', value: '' },
      postcode: '',
      country: getFieldValue(model?.country) || '',
      // Keep the text for display (label) but leave the id empty: an unvalidated
      // text edit is not a real NSW Point selection, so it must not surface a
      // `nswPointId`. A bogus id defeats the control's ability to restore the
      // previously selected address when the same suggestion is picked again.
      lookupValue: formattedAddress
        ? {
            label: formattedAddress,
            value: '',
            id: '',
          }
        : null,
      details: null,
    };
  }

  const streetAddress = getFieldValue(model?.streetAddress) || detailsAddress?.AddressLine1 || '';
  const propertyName = getFieldValue(model?.propertyName) || detailsAddress?.PropertyName || '';
  const suburb = getFieldValue(model?.suburb) || detailsAddress?.Suburb || '';
  const state = getFieldValue(model?.state) || detailsAddress?.State || '';
  const postcode = getFieldValue(model?.postcode) || detailsAddress?.Postcode || '';
  const country = getFieldValue(model?.country) || detailsAddress?.Country || 'Australia';
  const label = formattedAddress || [streetAddress, suburb, state, postcode].filter(Boolean).join(' ');
  const lookupId = getFieldValue(model?.nswPointId) || detailsAddress?.UniqueId || label;

  return {
    enterManually: isManualAddress,
    propertyName,
    addressLine: streetAddress,
    suburb,
    state: { label: state, text: state, value: state },
    postcode,
    country,
    lookupValue: label ? { label, value: lookupId, id: lookupId } : null,
    details: selectedDetails ?? null,
  };
};

export const normalizeAddressChange = (model, entryMode, changedPropertyName, isValidated, selectedDetails) => {
  const isManualAddress =
    entryMode === 'manual' || Boolean(changedPropertyName && changedPropertyName !== 'formattedAddress');
  const isValid = isValidatedAddressValue(model, isValidated);
  const useFormattedAddressOnly = shouldUseFormattedAddressOnly(isManualAddress, changedPropertyName, isValid);

  return {
    isValid,
    value: toLegacyAddressValue(
      model,
      isManualAddress,
      isValid && !isManualAddress ? selectedDetails ?? null : null,
      useFormattedAddressOnly,
    ),
  };
};

export const getAddressType = (searchUrl) =>
  searchUrl === process.env.REACT_APP_SEARCH_PHYSICAL_ADDRESS_URL ? 'physical' : 'all';
